import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {
  authorableKinds,
  authorableKindSchema,
  SCENE_KIND_NOUN,
} from '@platform/schema/content';
import { useState } from 'react';

import { DragList } from '../ordering/DragList';

import { addable, removable } from './order';

import type { AuthorableKind } from '@platform/schema/content';
import type { ReactNode } from 'react';

/**
 * The arc: which scenes a lesson has, and in what order — GEN-45.
 *
 * The dragging itself is `DragList`, which the path member list also uses. What is left
 * here is the part that is about scenes: the kind to add, and the ceiling on how many.
 */
export function SceneArc({
  count,
  children,
  onMove,
  onAdd,
  disabled,
}: {
  count: number;
  /** One node per scene, in order. Dragging reorders these. */
  children: ReactNode[];
  onMove: (from: number, to: number) => void;
  onAdd: (kind: AuthorableKind) => void;
  disabled: boolean;
}) {
  const [kind, setKind] = useState<AuthorableKind>('CONCEPT');

  const full = addable(count);

  return (
    <Stack spacing={1}>
      <DragList onMove={onMove} disabled={disabled}>
        {children}
      </DragList>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Add a scene"
          value={kind}
          disabled={disabled || full !== undefined}
          onChange={(event) => {
            setKind(authorableKindSchema.parse(event.target.value));
          }}
          sx={{ minWidth: 200 }}
        >
          {authorableKinds.map((option) => (
            <MenuItem key={option} value={option}>
              {SCENE_KIND_NOUN[option]}
            </MenuItem>
          ))}
        </TextField>

        {/* Wrapped, because a disabled button fires no events and would show no tooltip —
            which is the state the reason most needs saying. */}
        <Tooltip title={full ?? ''}>
          <span>
            <Button
              variant="outlined"
              disabled={disabled || full !== undefined}
              onClick={() => {
                onAdd(kind);
              }}
            >
              Add
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {full !== undefined && <Alert severity="info">{full}</Alert>}
    </Stack>
  );
}

/** Why this scene cannot be removed, shown where the control would have been. */
export function RemoveScene({
  count,
  disabled,
  onRemove,
}: {
  count: number;
  disabled: boolean;
  onRemove: () => void;
}) {
  const last = removable(count);

  return (
    <Tooltip title={last ?? ''}>
      <span>
        <Button
          size="small"
          color="error"
          disabled={disabled || last !== undefined}
          onClick={onRemove}
        >
          Remove scene
        </Button>
      </span>
    </Tooltip>
  );
}
